import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { isValidName, isValidPhone } from '../domain/userSettings.validators'
import { useUserDetails } from './user.hooks'

export const useUserSettingsForm = () => {
  const { t } = useTranslation()
  const userDetails = useUserDetails()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  const [phone, setPhone] = useState(userDetails?.phoneNumber || '')
  const [errors, setErrors] = useState<{ firstName?: string; lastName?: string; phone?: string }>({})

  useEffect(() => {
    if (!userDetails) return

    setFirstName(userDetails.firstName ?? '')
    setLastName(userDetails.lastName ?? '')
    setPhone(userDetails.phoneNumber ?? '')
    setErrors({})
  }, [userDetails])

  const handleFirstNameChange = (value: string) => {
    setFirstName(value)
    const isValid = isValidName(value)

    setErrors((prev) => ({
      ...prev,
      firstName: isValid ? '' : t('userSettingsPage.invalidFirstName'),
    }))
  }

  const handleLastNameChange = (value: string) => {
    setLastName(value)
    const isValid = isValidName(value)

    setErrors((prev) => ({
      ...prev,
      lastName: isValid ? '' : t('userSettingsPage.invalidLastName'),
    }))
  }

  const handlePhoneChange = (value: string) => {
    setPhone(value)
    const isValid = isValidPhone(value)

    setErrors((prev) => ({
      ...prev,
      phone: isValid ? '' : t('userSettingsPage.invalidPhone'),
    }))
  }

  const handleResetInformations = () => {
    setFirstName(userDetails?.firstName || '')
    setLastName(userDetails?.lastName || '')

    setPhone(userDetails?.phoneNumber || '')
  }

  const profileChanged = useMemo(() => {
    if (!userDetails) return false

    return (
      firstName !== (userDetails.firstName ?? '') ||
      lastName !== (userDetails.lastName ?? '') ||
      phone !== (userDetails.phoneNumber ?? '')
    )
  }, [firstName, lastName, phone, userDetails])

  return {
    firstName,
    lastName,
    phone,
    errors,
    profileChanged,
    handleFirstNameChange,
    handleLastNameChange,
    handlePhoneChange,
    handleResetInformations,
  }
}
